import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Link } from "react-router-dom";
import { PianoIcon } from "lucide-react";
import { SERVICE_TYPE_DESCRIPTIONS, SERVICE_TYPES } from "@/constants";
import { useSelector } from "react-redux";
import { getSelectedMover } from "@/store/mover/slice";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  faBoxesPacking,
  faBuilding,
  faGear,
  faHouse,
  faPeopleCarryBox,
  faPlus,
  faTruckLoading,
  faWarehouse,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Textarea } from "@/components/ui/textarea";

const Service: React.FC = () => {
  const { control, watch, setValue, getValues } = useFormContext();
  const mover = useSelector(getSelectedMover);
  const service = watch("service");

  useEffect(() => {
    const service = getValues("service");

    if (service !== "residential") {
      setValue("kind", null);
    }

    if (service !== "storage" && service !== "residential") {
      setValue("size", null);
    }

    if (service !== "commercial" && service !== "residential") {
      setValue("area", null);
    }
  }, [service, getValues, setValue]);

  return (
    <div className="relative">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row gap-6 md:gap-4">
          <FormField
            control={control}
            name="service"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex flex-row justify-between items-center gap-2">
                  Select the service
                  <FormMessage />
                  <div className="text-xs text-muted-foreground">
                    <Link
                      className="flex flex-row items-center gap-0.5"
                      to="/mover-settings/operating-policies"
                      target="_blank"
                    >
                      <FontAwesomeIcon icon={faGear} className="w-3 h-3" />
                      Configure
                    </Link>
                  </div>
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    defaultValue="move"
                    className="grid gap-2"
                  >
                    {mover?.mover_configuration?.services_offered
                      ?.filter((service) => service.enabled)
                      .map((service) => (
                        <div key={service.name}>
                          <RadioGroupItem
                            value={service.name}
                            id={`service-${service.name}`}
                            className="peer sr-only"
                          />
                          <Label
                            htmlFor={`service-${service.name}`}
                            className="flex flex-row items-baseline gap-2 rounded-lg border-2 border-stone-200 bg-popover px-4 py-1 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary peer-data-[state=checked]:bg-amber-50 [&:has([data-state=checked])]:bg-amber-50 cursor-pointer"
                          >
                            {service.name === "residential" && (
                              <FontAwesomeIcon
                                icon={faHouse}
                                className="h-3 w-3"
                              />
                            )}
                            {service.name === "commercial" && (
                              <FontAwesomeIcon
                                icon={faBuilding}
                                className="h-3 w-3"
                              />
                            )}
                            {service.name === "storage" && (
                              <FontAwesomeIcon
                                icon={faWarehouse}
                                className="h-3 w-3"
                              />
                            )}
                            {service.name === "labor" && (
                              <FontAwesomeIcon
                                icon={faPeopleCarryBox}
                                className="h-3 w-3"
                              />
                            )}
                            {service.name === "items" && (
                              <FontAwesomeIcon
                                icon={faBoxesPacking}
                                className="h-3 w-3"
                              />
                            )}
                            {service.name === "piano" && (
                              <PianoIcon className="h-3 w-3" />
                            )}
                            {service.name === "junk" && (
                              <FontAwesomeIcon
                                icon={faTruckLoading}
                                className="h-3 w-3"
                              />
                            )}
                            {service.name === "trash" && (
                              <FontAwesomeIcon
                                icon={faTruckLoading}
                                className="h-3 w-3"
                              />
                            )}
                            {service.name === "custom" && (
                              <FontAwesomeIcon
                                icon={faPlus}
                                className="h-3 w-3"
                              />
                            )}
                            <div className="flex flex-col justify-start text-sm">
                              <span className="font-semibold">
                                {SERVICE_TYPES[
                                  service.name as keyof typeof SERVICE_TYPES
                                ] || service.name}
                              </span>
                              <span className="text-muted-foreground">
                                {
                                  SERVICE_TYPE_DESCRIPTIONS[
                                    service.name as keyof typeof SERVICE_TYPE_DESCRIPTIONS
                                  ]
                                }
                              </span>
                            </div>
                          </Label>
                        </div>
                      ))}
                  </RadioGroup>
                </FormControl>
              </FormItem>
            )}
          />

          {watch("service") === "residential" && (
            <FormField
              control={control}
              name="kind"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Select Residence Type
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      defaultValue="apartment"
                      className="grid gap-2"
                    >
                      <div>
                        <RadioGroupItem
                          value="apartment"
                          id="kind-apartment"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="kind-apartment"
                          className="flex flex-row items-center gap-2 rounded-lg border-2 border-muted bg-popover px-4 py-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="flex-1">Apartment</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="sfh"
                          id="kind-sfh"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="kind-sfh"
                          className="flex flex-row items-center gap-2 rounded-lg border-2 border-muted bg-popover px-4 py-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="flex-1">Home</div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
          )}

          {watch("service") === "residential" && (
            <FormField
              control={control}
              name="size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Size</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value?.toString()}
                      className="grid gap-2"
                    >
                      <div>
                        <RadioGroupItem
                          value="Studio"
                          id="rooms-0"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="rooms-0"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">Studio</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="1BR"
                          id="rooms-1"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="rooms-1"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">1BR</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="2BR"
                          id="rooms-2"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="rooms-2"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">2BR</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="3BR"
                          id="rooms-3"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="rooms-3"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">3BR</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="4BR"
                          id="rooms-4"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="rooms-4"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">4BR</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="5BR"
                          id="rooms-5"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="rooms-5"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">5BR</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="6+BR"
                          id="rooms-6"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="rooms-6"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">6+BR</div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {watch("service") === "storage" && (
            <FormField
              control={control}
              name="size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Storage Unit Size</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="grid gap-2"
                    >
                      <div>
                        <RadioGroupItem
                          value="5x5"
                          id="size-5x5"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="size-5x5"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">5x5</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="5x10"
                          id="size-5x10"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="size-5x10"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">5x10</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="5x15"
                          id="size-5x15"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="size-5x15"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">5x15</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="10x10"
                          id="size-10x10"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="size-10x10"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">10x10</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="10x15"
                          id="size-10x15"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="size-10x15"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">10x15</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="10x20"
                          id="size-10x20"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="size-10x20"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">10x20</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="10x25"
                          id="size-10x25"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="size-10x25"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">10x25</div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="10x30"
                          id="size-10x30"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="size-10x30"
                          className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover px-3 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                        >
                          <div className="text-center">10x30</div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* change to dropdown yes or no */}
          {/* show only if kind is sfh */}
          {/* {watch("kind") !== "apartment" &&
          watch("kind") !== "storage" &&
          watch("kind") !== "commercial" &&
          watch("kind") !== "single_item" ? (
            <FormField
              control={control}
              name="includes_garage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Garage?</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value?.toString()}
                      onValueChange={(value) =>
                        field.onChange(value === "true")
                      }
                    >
                      <SelectTrigger className="w-full md:w-[75px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null} */}

          {/* area, applies to all but single item */}
          {(watch("service") === "residential" ||
            watch("service") === "commercial") && (
            <FormField
              control={control}
              name="area"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Area (sq ft)</FormLabel>
                  <FormControl>
                    <div className="flex items-center">
                      <Input
                        className="w-24 rounded-r-none"
                        {...field}
                        type="number"
                        value={field.value ?? ""}
                      />
                      {/* <span className="inline-flex items-center px-2 rounded-r-md border border-l-0 border-input bg-background text-black text-sm h-8">
                        sq. ft.
                      </span> */}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={control}
            name="notes_service"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service Notes</FormLabel>
                <FormControl>
                  <Textarea
                    rows={6}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {/* add long carry */}
          {/* <FormField
            control={control}
            name="long_carry"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Long Carry?</FormLabel>
                <FormControl>
                  <Select
                    value={field.value?.toString()}
                    onValueChange={(value) => field.onChange(value === "true")}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          /> */}
        </div>
      </div>
    </div>
  );
};

export default Service;
